import { ConditionsNew } from '@retail/calculus/types';
import { Stack } from '@mui/material';
import { AddButton, CalculusActionPortal, DeleteDialog, Link } from '@retail/components';
import { RoundingConditionsTable } from '@retail/calculus/components';
import { useDisclosure, usePersistentNavigation } from '@shared/hooks';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import CreatePriceRoundingConditionDialog from './CreatePriceRoundingConditionDialog';
import { useState } from 'react';
import { useConditionActions } from '@retail/calculus/context';
import { EditRoundingConditionDialog } from './EditRoundingConditionDialog';
import { LookupRoundingRuleDialog } from '@retail/rounding/containers';
import { SupplierBase } from '@retail/suppliers/types';
import { mgPrisRoutes } from '@retail/mgpris/config';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';

const roundingRulesPath = mgPrisRoutes.administration.rounding.getFullLinkPath();

interface Props {
  conditions: ConditionsNew[];
  isFetching: boolean;
  supplier?: SupplierBase;
}

export function RoundingConditionsContainer({ conditions, isFetching, supplier }: Props) {
  const { getPersistentSearchParams } = usePersistentNavigation();

  const createConditionDialog = useDisclosure();
  const t = useCalculusTFunction();
  const [conditionIdToDeactivate, setConditionIdToDeactivate] = useState<number>();
  const [conditionToEdit, setConditionToEdit] = useState<ConditionsNew>();
  const [conditionIdToDelete, setConditionIdToDelete] = useState<number>();
  const [simulationRuleId, setSimulationRuleId] = useState<number>();

  const { userCanCreate } = usePermissionsForCurrentUser();
  const userCanCreatePriceRounding = userCanCreate('PRICE_CALCULATION__ROUNDING');
  const { userCanUpdate } = usePermissionsForCurrentUser();
  const canUpdatePriceRoundingConditions = userCanUpdate('PRICE_CALCULATION__ROUNDING');

  const { removeCondition } = useConditionActions();

  const conditionActions = canUpdatePriceRoundingConditions
    ? {
        setDeactivateId: setConditionIdToDeactivate,
        setEdit: setConditionToEdit,
        setDeleteId: setConditionIdToDelete,
      }
    : undefined;

  return (
    <Stack flexGrow={1}>
      <CalculusActionPortal>
        <Link
          to={{ pathname: roundingRulesPath, search: getPersistentSearchParams(roundingRulesPath) }}
        >
          {t('calculus.rounding.roundingRulesLink')}
          <ArrowRightAltRoundedIcon />
        </Link>
        <AddButton onClick={createConditionDialog.onOpen} disabled={!userCanCreatePriceRounding}>
          {t('calculus.rounding.createCondition')}
        </AddButton>
      </CalculusActionPortal>
      <RoundingConditionsTable
        roundingConditions={conditions}
        loading={isFetching}
        openPriceSimulation={setSimulationRuleId}
        openCreateDialog={createConditionDialog.onOpen}
        conditionActions={conditionActions}
      />
      {createConditionDialog.isOpen && (
        <CreatePriceRoundingConditionDialog
          open={createConditionDialog.isOpen}
          onClose={createConditionDialog.onClose}
          supplier={supplier}
        />
      )}
      {conditionToEdit && canUpdatePriceRoundingConditions && (
        <EditRoundingConditionDialog
          roundingCondition={conditionToEdit}
          onClose={() => setConditionToEdit(undefined)}
        />
      )}
      {conditionIdToDeactivate && canUpdatePriceRoundingConditions && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deactivateCondition')}
          message={t('calculus.actions.confirmDeactivation')}
          onDelete={() => removeCondition([conditionIdToDeactivate], 'deactivate')}
          onClose={() => setConditionIdToDeactivate(undefined)}
        />
      )}
      {conditionIdToDelete && canUpdatePriceRoundingConditions && (
        <DeleteDialog
          isOpen
          dialogTitle={t('calculus.actions.deleteFromDraft')}
          message={t('calculus.actions.confirmRemoval')}
          onDelete={() => removeCondition([conditionIdToDelete], 'delete')}
          onClose={() => setConditionIdToDelete(undefined)}
        />
      )}
      {simulationRuleId && (
        <LookupRoundingRuleDialog
          open
          onClose={() => setSimulationRuleId(undefined)}
          priceRoundingRuleId={simulationRuleId}
        />
      )}
    </Stack>
  );
}
